// client/src/App.js
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext.js';
import { SettingsProvider } from './contexts/SettingsContext.js';
import { ThemeProvider } from './hooks/useTheme.js';
import { useAuth } from './hooks/useAuth.js';

import Navbar from './components/layout/Navbar.js';
import Footer from './components/layout/Footer.js';

// Pages
import Home from './pages/Home.js';
import About from './pages/About.js';
import Dashboard from './pages/Dashboard.js';
import Moonbeams from './pages/Moonbeams.js';
import MoonbeamView from './pages/MoonbeamView.js';
import MyMoonbeams from './pages/MyMoonbeams.js';
import Stories from './pages/Stories.js';
import StoryDetail from './pages/StoryDetail.js';
import Contacts from './pages/Contacts.js';
import Account from './pages/Account.js';
import AccountAnalytics from './pages/AccountAnalytics.js';
import BusinessProfile from './pages/BusinessProfile.js';
import Pricing from './pages/Pricing.js';
import Checkout from './pages/Checkout.js';
import ChangePlan from './pages/ChangePlan.js';
import ChoosePlan from './pages/ChoosePlan.js';
import GiftSubscription from './pages/GiftSubscription.js';
import RedeemGift from './pages/RedeemGift.js';
import AcceptGift from './pages/AcceptGift.js';
import MyGifts from './pages/MyGifts.js';
import Privacy from './pages/Privacy.js';
import Terms from './pages/Terms.js';
import Rules from './pages/Rules.js'; 
// Auth pages
import Login from './pages/auth/Login.js';
import Register from './pages/auth/Register.js';
import ForgotPassword from './pages/auth/ForgotPassword.js';

// Admin pages
import AdminDashboard from './pages/admin/AdminDashboard.js';
import ManageTemplates from './pages/admin/ManageTemplates.js';
import ManageMusic from './pages/admin/ManageMusic.js';
import ManageCategories from './pages/admin/ManageCategories.js';
import ManageSettings from './pages/admin/ManageSettings.js';
import ManageLinks from './pages/admin/ManageLinks.js';
import AdminAnalytics from './pages/admin/AdminAnalytics.js';
import CtaResults from './pages/admin/CtaResults.js';
import ManageUsers from './pages/admin/ManageUsers.js';
import ManageUserTypes from './pages/admin/ManageUserTypes.js';
import ManageFieldDefinitions from './pages/admin/ManageFieldDefinitions.js';
import ManageTemplateFields from './pages/admin/ManageTemplateFields.js';
import ManageTemplateParts from './pages/admin/ManageTemplateParts.js';
import ManageGifts from './pages/admin/ManageGifts.js';
import ManageGiftEmailTemplates from './pages/admin/ManageGiftEmailTemplates.js';
import ManageNotificationEmailTemplates from './pages/admin/ManageNotificationEmailTemplates.js';
import ManageUnsubscribeTemplates from './pages/admin/ManageUnsubscribeTemplates.js';
import ManageStories from './pages/admin/ManageStories.js';
import StoryEditor from './pages/admin/StoryEditor.js';
import ManageStorySubcategories from './pages/admin/ManageStorySubcategories.js';
import ManageMarketingCampaigns from './pages/admin/ManageMarketingCampaigns.js';
import ManageSendingDomains from './pages/admin/ManageSendingDomains.js';

// Moonbeam creator
import MoonbeamCreator from './components/moonbeams/MoonbeamCreator.js';

// Route guards
function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="text-center py-20 text-royal-200">Loading...</div>;
  return user ? children : <Navigate to="/login" />;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="text-center py-20 text-royal-200">Loading...</div>;
  return user?.isadmin ? children : <Navigate to="/" />;
}

function BusinessRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="text-center py-20 text-royal-200">Loading...</div>;
  return user?.tier?.canuselinks ? children : <Navigate to="/account" />;
}

function AppRoutes() {
  const location = useLocation();
  
  // Hide main navbar and footer on eCard view pages (both old and new URL formats)
  const isEcardView = location.pathname.startsWith('/moonbeam/') || location.pathname.startsWith('/card/');
  
  return (
    <div className="min-h-screen flex flex-col bg-cream dark:bg-bg-dark transition-colors duration-300">
      {!isEcardView && <Navbar />}
      <main className="flex-1">
        <Routes>
          {/* Public */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/moonbeams" element={<Moonbeams />} />
          <Route path="/moonbeam/:uuid" element={<MoonbeamView />} />
          <Route path="/card/:uuid" element={<MoonbeamView />} />
          <Route path="/stories" element={<Stories />} />
          <Route path="/stories/:slug" element={<StoryDetail />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/rules" element={<Rules />} />
          <Route path="/moonbeams/create/:templateId" element={<MoonbeamCreator />} />

          {/* Gift — public pages */}
          <Route path="/gift" element={<GiftSubscription />} />
          <Route path="/gift/accept" element={<AcceptGift />} />
          <Route path="/gift/redeem/:giftcode" element={<RedeemGift />} />

          {/* Auth */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          {/* Protected */}
          <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="/my-moonbeams" element={<PrivateRoute><MyMoonbeams /></PrivateRoute>} />
          <Route path="/contacts" element={<PrivateRoute><Contacts /></PrivateRoute>} />
          <Route path="/account" element={<PrivateRoute><Account /></PrivateRoute>} />
          <Route path="/checkout" element={<PrivateRoute><Checkout /></PrivateRoute>} />
          <Route path="/account/change-plan" element={<PrivateRoute><ChangePlan /></PrivateRoute>} />
          <Route path="/choose-plan" element={<PrivateRoute><ChoosePlan /></PrivateRoute>} />
          <Route path="/gift/my-gifts" element={<PrivateRoute><MyGifts /></PrivateRoute>} />

          {/* Business tier features */}
          <Route path="/account/analytics" element={<BusinessRoute><AccountAnalytics /></BusinessRoute>} />
          <Route path="/account/business-profile" element={<BusinessRoute><BusinessProfile /></BusinessRoute>} />

          {/* Admin */}
          <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/admin/templates" element={<AdminRoute><ManageTemplates /></AdminRoute>} />
          <Route path="/admin/music" element={<AdminRoute><ManageMusic /></AdminRoute>} />
          <Route path="/admin/stories" element={<AdminRoute><ManageStories /></AdminRoute>} />
          <Route path="/admin/categories" element={<AdminRoute><ManageCategories /></AdminRoute>} />
          <Route path="/admin/settings" element={<AdminRoute><ManageSettings /></AdminRoute>} />
          <Route path="/admin/links" element={<AdminRoute><ManageLinks /></AdminRoute>} />
          <Route path="/admin/analytics" element={<AdminRoute><AdminAnalytics /></AdminRoute>} />
          <Route path="/admin/cta-results" element={<AdminRoute><CtaResults /></AdminRoute>} />
          <Route path="/admin/users" element={<AdminRoute><ManageUsers /></AdminRoute>} />
          <Route path="/admin/user-types" element={<AdminRoute><ManageUserTypes /></AdminRoute>} />
          <Route path="/admin/sending-domains" element={<AdminRoute><ManageSendingDomains /></AdminRoute>} />
          <Route path="/admin/field-definitions" element={<ManageFieldDefinitions />} />
          <Route path="/admin/templates/:templateId/fields" element={<ManageTemplateFields />} />
          <Route path="/admin/templates/:templateId/parts" element={<AdminRoute><ManageTemplateParts /></AdminRoute>} />
          <Route path="/admin/gifts" element={<AdminRoute><ManageGifts /></AdminRoute>} />
          <Route path="/admin/gift-email-templates" element={<AdminRoute><ManageGiftEmailTemplates /></AdminRoute>} />
          <Route path="/admin/notification-email-templates" element={<AdminRoute><ManageNotificationEmailTemplates /></AdminRoute>} />
          <Route path="/admin/unsubscribe-templates" element={<AdminRoute><ManageUnsubscribeTemplates /></AdminRoute>} />
          <Route path="/admin/marketing" element={<AdminRoute><ManageMarketingCampaigns /></AdminRoute>} />
          <Route path="/admin/stories/new" element={<StoryEditor />} />
          <Route path="/admin/stories/edit/:id" element={<StoryEditor />} />
          <Route path="/admin/subcategories" element={<ManageStorySubcategories />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
      {!isEcardView && <Footer />}
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <SettingsProvider>
          <ThemeProvider>
            <AppRoutes />
          </ThemeProvider>
        </SettingsProvider>
      </AuthProvider>
    </Router>
  );
}